// ---- Quest step coach (client-only tutorial guidance) ----
// Content-driven: an optional `coach` block on a quest step walks the player
// through equipping/selecting an item and/or going to a world point — without
// bespoke UI per quest. Soft-landing exemplars:
//   catch-fish: equip fishing-rod → arrow at the river
//   cut-logs:   equip hatchet     → arrow at the oak
//   light-fire: select kindling   → arrow on the bank (+ Start fire glow)
//   cook-fish:  pointNearestFire  → arrow over the live campfire
//
// Presentation only — never on the wire, never simulated server-side. Phases:
//   1. equip/select pending: glow #invToggle; when bag open, glow matching
//      inv slots (+ Equip when that slot is selected, for equip coaches)
//   2. equip/select done (or no bag phase): yellow world arrow + ground ring
// Hide everything while talking / cinematics / fishing mid-cast / KO.

import * as THREE from "three";
import { game, inputLocked } from "./state.ts";
import { currentWaypoint } from "./quest.ts";
import { questById } from "./quest/index.ts";
import { makeWaypointRing } from "./questhud.ts";
import {
  countOf, equippedTool, equippedType, coachGlowItem, setCoachGlowItem,
  inventoryOpen, selectedType,
} from "./inventory.ts";
import { mapById } from "./content/load.ts";
import { ACTIVE_MAP_ID } from "./map/active.ts";
import { heightAt } from "./terrain.ts";
import { nearestChopTarget } from "./interact.ts";
import { getFires } from "./campfire.ts";
import { liveById } from "./npc/index.ts";
import type { ItemType } from "./shared.ts";
import type { QuestCoach } from "./quest/define.ts";

let camera: THREE.Camera | null = null;
let ring: THREE.Group | null = null;
let markerEl: HTMLElement | null = null;
let invToggle: HTMLElement | null = null;
let equipBtn: HTMLButtonElement | null = null;
let fireStartBtn: HTMLButtonElement | null = null;
const _v = new THREE.Vector3();

/** Resolved coach for this frame — null when nothing to teach. */
export type CoachState = {
  coach: QuestCoach;
  /** Still waiting on equip. */
  needEquip: boolean;
  /** Still waiting on bag selection (kindling, etc.). */
  needSelect: boolean;
  /** Item type to highlight (equip/select phase). */
  glowType: ItemType | null;
  /** Bag is open — drives bag-button vs slot glow. */
  bagOpen: boolean;
  /** Glow the Start fire bag button (select done + kindling selected). */
  glowFireStart: boolean;
  /** World target for the yellow arrow (when !needEquip && !needSelect). */
  point: { x: number; z: number; label?: string } | null;
};

let last: CoachState | null = null;

export function initCoach(scene: THREE.Scene, cam: THREE.Camera): void {
  camera = cam;
  invToggle = document.getElementById("invToggle");
  equipBtn = document.getElementById("equipBtn") as HTMLButtonElement | null;
  fireStartBtn = document.getElementById("fireStartBtn") as HTMLButtonElement | null;
  markerEl = document.getElementById("coachmarker");
  if (!ring) {
    ring = makeWaypointRing();
    ring.visible = false;
    scene.add(ring);
  }
  refreshCoach();
}

/** Recompute coach from quest + inventory. Call after setQuests / bag / equip. */
export function refreshCoach(): void {
  last = resolveCoach();
  applyHud(last);
}

/** Per-frame: seat the world arrow / ring. Call from the game loop. */
export function updateCoach(time: number): void {
  if (!camera || !ring || !markerEl) return;
  // Re-resolve cheaply — equip/bag can change without a dedicated event.
  const next = resolveCoach();
  if (!coachEqual(last, next)) {
    last = next;
    applyHud(last);
  }

  const pt = last?.point ?? null;
  const showWorld = !!(pt && game.joined && !coachSuppressed());
  if (!showWorld || !pt) {
    ring.visible = false;
    markerEl.style.display = "none";
    return;
  }

  const y = heightAt(pt.x, pt.z);
  // Same ground-ring motion as the talk/kill waypoint (questhud.ts) — gentle
  // pulse + slow spin. Screen arrow + "Fish here" label stay separate below.
  ring.visible = true;
  ring.position.set(pt.x, y + 0.05, pt.z);
  const pulse = 1 + Math.sin(time * 3) * 0.08;
  ring.scale.set(pulse, pulse, pulse);
  ring.rotation.y = time * 0.5;

  // Project to screen; if behind/off-screen, clamp to the frame edge so the
  // arrow always points the way (edge-seeking coach, not a lost offscreen mark).
  _v.set(pt.x, y + 1.4, pt.z).project(camera);
  const W = window.innerWidth, H = window.innerHeight;
  let sx = (_v.x * 0.5 + 0.5) * W;
  let sy = (-_v.y * 0.5 + 0.5) * H;
  const behind = _v.z > 1;
  const pad = 28;
  if (behind) {
    // Flip to the opposite edge when the target is behind the camera.
    sx = W - sx;
    sy = H - sy;
  }
  const onScreen =
    !behind && sx >= pad && sx <= W - pad && sy >= pad && sy <= H - pad;
  sx = Math.min(W - pad, Math.max(pad, sx));
  sy = Math.min(H - pad, Math.max(pad, sy));

  markerEl.style.display = "block";
  markerEl.style.left = `${sx}px`;
  markerEl.style.top = `${sy}px`;
  markerEl.classList.toggle("edge", !onScreen);
  const label = markerEl.querySelector(".coach-label") as HTMLElement | null;
  if (label) {
    label.textContent = pt.label ?? "";
    label.style.display = pt.label ? "block" : "none";
  }
}

// ---- Resolve ---------------------------------------------------------------

function resolveCoach(): CoachState | null {
  if (!game.joined || coachSuppressed()) return null;
  const wp = currentWaypoint();
  if (!wp) return null;
  const step = questById(wp.questId)?.steps.find((s) => s.id === wp.stepId);
  const coach = step?.coach;
  if (!coach) return null;

  let needEquip = false;
  let needSelect = false;
  let glowType: ItemType | null = null;
  let glowFireStart = false;

  if (coach.equip) {
    const type = coach.equip.type as ItemType;
    const as = coach.equip.as ?? "tool";
    // tool channel = rod OR hatchet (equippedTool); weapon channel = main hand.
    const equipped = as === "weapon" ? equippedType() === type : equippedTool() === type;
    if (!equipped) {
      // Only coach equip if they hold the item (post-gift). No tool → no coach
      // (re-open Dekkar's gift line); don't point at the tree bare-handed.
      if (countOf(type) > 0) {
        needEquip = true;
        glowType = type;
      } else {
        return null;
      }
    }
  }

  if (coach.select && !needEquip) {
    const type = coach.select.type as ItemType;
    if (countOf(type) <= 0) return null; // no kindling yet — re-open gift line
    if (selectedType() !== type) {
      needSelect = true;
      glowType = type;
    } else {
      // Selected — nudge the Start fire button when the bag is open.
      glowFireStart = inventoryOpen();
    }
  }

  let point: CoachState["point"] = null;
  if (!needEquip && !needSelect) {
    point = resolvePoint(coach);
    // Cross-map guides (e.g. "south road" before a kill on the next map): drop
    // the fixed point once the objective NPC is live here — the gold kill
    // diamond takes over.
    if (point && wp.targetNpcId && targetNpcLive(wp.targetNpcId)) {
      point = null;
    }
  }

  if (!needEquip && !needSelect && !point) return null;
  return {
    coach,
    needEquip,
    needSelect,
    glowType,
    bagOpen: inventoryOpen(),
    glowFireStart,
    point,
  };
}

/** True when the spawn is in the live client roster and interest-visible.
 *  Interest uses `netHidden` as source of truth — do NOT gate on `group.visible`
 *  (cutaway rewrites that every frame and would desync coach vs minimap). */
function targetNpcLive(spawnId: string): boolean {
  const L = liveById(spawnId);
  return !!(L && !L.defeated && !L.netHidden);
}

/** Resolve order: explicit `point` → `pointWater` → `pointNearestChop` →
 *  `pointNearestFire`. */
function resolvePoint(coach: QuestCoach): CoachState["point"] {
  if (coach.point) {
    // Map-scoped guides: hide once the player has transferred away (or before
    // they arrive on the map the point is authored for).
    if (coach.point.map && coach.point.map !== ACTIVE_MAP_ID) return null;
    return { x: coach.point.x, z: coach.point.z, label: coach.point.label ?? "Go here" };
  }
  if (coach.pointWater) {
    const map = mapById(ACTIVE_MAP_ID);
    const w = map?.waters?.find((z) => z.id === coach.pointWater);
    if (!w) return null;
    // Prefer the shore nearest the player (or map origin) so the arrow lands at
    // the cast edge of a lake, not the middle of a 12u radius.
    const px = game.player?.position.x ?? 0;
    const pz = game.player?.position.z ?? 0;
    const dx = px - w.x, dz = pz - w.z;
    const len = Math.hypot(dx, dz) || 1;
    // Just inside the water rim toward the player (still wet, walk-to-cast).
    const inset = Math.max(1.5, (w.r ?? 8) - 1.5);
    const t = Math.min(1, inset / len);
    return { x: w.x + dx * t, z: w.z + dz * t, label: "Fish here" };
  }
  if (coach.pointNearestChop) {
    const t = nearestChopTarget();
    if (!t) return null;
    return { x: t.x, z: t.z, label: "Cut here" };
  }
  if (coach.pointNearestFire) {
    const t = nearestFireTarget();
    if (!t) return null;
    return { x: t.x, z: t.z, label: "Cook here" };
  }
  return null;
}

/** Nearest live campfire — prefer the player's own, else any burning fire. */
function nearestFireTarget(): { x: number; z: number } | null {
  const fires = getFires();
  if (!fires.length) return null;
  const px = game.player?.position.x ?? 0;
  const pz = game.player?.position.z ?? 0;
  const pool = fires.some((f) => f.mine) ? fires.filter((f) => f.mine) : fires;
  let best = pool[0]!;
  let bestD = Infinity;
  for (const f of pool) {
    const d = Math.hypot(f.x - px, f.z - pz);
    if (d < bestD) {
      bestD = d;
      best = f;
    }
  }
  return { x: best.x, z: best.z };
}

function coachSuppressed(): boolean {
  // Reuse THE canonical input-lock predicate so this can't drift (it already
  // missed `aiming` — the exact copy-paste rot state.ts warns about).
  return inputLocked();
}

function coachEqual(a: CoachState | null, b: CoachState | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.needEquip === b.needEquip
    && a.needSelect === b.needSelect
    && a.glowType === b.glowType
    && a.bagOpen === b.bagOpen
    && a.glowFireStart === b.glowFireStart
    && a.point?.x === b.point?.x
    && a.point?.z === b.point?.z
    && a.point?.label === b.point?.label;
}

// ---- HUD application -------------------------------------------------------

function applyHud(state: CoachState | null): void {
  // Phase 1a: bag CLOSED → pulse the backpack button only.
  // Phase 1b: bag OPEN   → pulse the item slot(s) (+ Equip when selected).
  // Phase 2:  equipped/selected → no bag glow; world arrow (and maybe Start fire).
  const needBag = !!(state?.needEquip || state?.needSelect);
  const bagOpen = !!state?.bagOpen;
  const glowType = needBag && state?.glowType ? state.glowType : null;

  invToggle?.classList.toggle("coach-glow", needBag && !bagOpen);

  // Slot glow only while the bag is open (so it "starts" when they click the bag).
  const slotGlow = needBag && bagOpen ? glowType : null;
  if (coachGlowItem() !== slotGlow) {
    setCoachGlowItem(slotGlow);
  }

  // Equip button glow is owned by inventory.render (selected + coach item).
  if (!slotGlow) equipBtn?.classList.remove("coach-glow");

  // Start fire: glow once kindling is selected (select coach phase complete).
  if (fireStartBtn) {
    const show = !!(state?.glowFireStart && fireStartBtn.style.display !== "none");
    fireStartBtn.classList.toggle("coach-glow", show);
  }
}
